/**
 * First-run hand-off for brand-new accounts.
 *
 * The first real registrant (2026-08-26) signed up, landed on an empty
 * bookshelf, opened the library and left within 60 seconds without ever
 * seeing the create form. A fresh account should land *in* the create form
 * with an idea already filled in, not on an empty home screen.
 *
 * The flag is written when an account is created (password register, or a
 * Google/Apple sign-in that created the account) and consumed by HomePage,
 * which is where PublicRoute's post-login redirect always ends up — so the
 * hand-off works regardless of which redirect wins the race. It expires
 * quickly and is skipped when the user already has stories (a claimed trial
 * story wins) or an explicit `next` destination exists.
 */
const KEY = 'snugtale_fresh_account';
const TTL_MS = 5 * 60 * 1000;

export const FRESH_CREATE_PATH = '/create?fresh=1';

export function markFreshAccount(): void {
  try {
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch { /* ignore */ }
}

export function clearFreshAccount(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch { /* ignore */ }
}

/**
 * Theme hand-off from the reader's closing slide (native apps): the story a
 * visitor just finished is the best hint for their first own story. Stored
 * next to the fresh-account flag and consumed once by the create picker,
 * which pre-selects the chip (interest key) or the typed topic (localized
 * catalog label). Never sent to the server by itself.
 */
const THEME_KEY = 'snugtale_signup_theme';
const THEME_TTL_MS = 30 * 60 * 1000;

export type SignupTheme = { kind: 'key'; key: string } | { kind: 'custom'; label: string };

export function stashSignupTheme(theme: SignupTheme | null): void {
  try {
    if (!theme) { sessionStorage.removeItem(THEME_KEY); return; }
    sessionStorage.setItem(THEME_KEY, JSON.stringify({ ...theme, ts: Date.now() }));
  } catch { /* ignore */ }
}

/** Returns the stashed theme once, or null when absent, malformed or stale. */
export function consumeSignupTheme(): SignupTheme | null {
  try {
    const raw = sessionStorage.getItem(THEME_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(THEME_KEY);
    const parsed = JSON.parse(raw) as { kind?: string; key?: string; label?: string; ts?: number };
    if (typeof parsed.ts !== 'number' || Date.now() - parsed.ts > THEME_TTL_MS) return null;
    if (parsed.kind === 'key' && typeof parsed.key === 'string' && parsed.key) return { kind: 'key', key: parsed.key };
    if (parsed.kind === 'custom' && typeof parsed.label === 'string' && parsed.label.trim()) return { kind: 'custom', label: parsed.label.trim() };
    return null;
  } catch {
    return null;
  }
}

/**
 * The first run's account-first variant (2026-10-02, docs/NATIVE-FIRST-RUN-2026-10.md): on a child-declared Android
 * install the parent passes the gate, picks name, age and idea on the first-run screens, and the submit opens the
 * sign-up instead of an anonymous trial. The choice waits here (30 min) and the first story is created in the new
 * account from it (HomePage, after sign-up). RegisterPage pre-fills name and age from it.
 *
 * localStorage, not sessionStorage (2026-10-02): the Facebook sign-up runs in the system browser and the WebView may be
 * recreated meanwhile (lib/facebookAuth.ts keeps its PKCE intent in Preferences for the same reason) — a recreated
 * WebView starts with an empty sessionStorage and the parent's idea would be gone. The 30-minute stamp still bounds it,
 * and a stale entry is removed whenever it is read (HomePage reads it on every mount).
 */
const FIRST_RUN_STORY_KEY = 'snugtale_first_run_story';
const FIRST_RUN_STORY_TTL_MS = 30 * 60 * 1000;

export interface FirstRunStory {
  theme: string;
  /** Chip key the idea came from (stories.theme_tag), null for typed text. */
  theme_tag: string | null;
  /** Cleaned first name (lib/trialEpisode.cleanName) or null. */
  child_name: string | null;
  /** 4 | 6 | 8 | 10 from the age chips, or null when skipped. */
  child_age: number | null;
  language: string;
}

export function stashFirstRunStory(story: FirstRunStory): void {
  try {
    localStorage.setItem(FIRST_RUN_STORY_KEY, JSON.stringify({ ...story, ts: Date.now() }));
  } catch { /* ignore */ }
}

function readFirstRunEntry(): { story: FirstRunStory; ts: number } | null {
  try {
    const raw = localStorage.getItem(FIRST_RUN_STORY_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<FirstRunStory> & { ts?: number };
    if (typeof p.ts !== 'number' || Date.now() - p.ts > FIRST_RUN_STORY_TTL_MS) {
      localStorage.removeItem(FIRST_RUN_STORY_KEY);
      return null;
    }
    if (typeof p.theme !== 'string' || p.theme.trim().length < 3 || typeof p.language !== 'string') return null;
    return {
      story: {
        theme: p.theme.trim(),
        theme_tag: typeof p.theme_tag === 'string' && p.theme_tag ? p.theme_tag : null,
        child_name: typeof p.child_name === 'string' && p.child_name.trim() ? p.child_name.trim() : null,
        child_age: typeof p.child_age === 'number' && p.child_age >= 3 && p.child_age <= 10 ? p.child_age : null,
        language: p.language,
      },
      ts: p.ts,
    };
  } catch {
    return null;
  }
}

function readFirstRunStory(): FirstRunStory | null {
  return readFirstRunEntry()?.story ?? null;
}

/** The waiting choice without consuming it (RegisterPage's pre-fill; FirstRunPage's seed after a back from /register). */
export function peekFirstRunStory(): FirstRunStory | null {
  return readFirstRunStory();
}

/** When the waiting choice was last written (the hand-off or RegisterPage's mirror of an edit), or null when there is
 *  none: FirstRunPage keeps whichever is newer, this choice or the visit's own answers (2026-10-02 review). */
export function firstRunStoryStashedAt(): number | null {
  return readFirstRunEntry()?.ts ?? null;
}

/** The waiting choice, once (HomePage creates the story from it). */
export function consumeFirstRunStory(): FirstRunStory | null {
  const story = readFirstRunStory();
  try { localStorage.removeItem(FIRST_RUN_STORY_KEY); } catch { /* ignore */ }
  return story;
}

/**
 * The exact idea text for the create form (2026-10-02): when the first run's account story cannot be started after
 * sign-up (402, 403, a screener decline, the network), HomePage sends the parent to the fresh create form with the
 * idea already in the text box — a chip or a custom topic alone (stashSignupTheme) would only bring new idea cards,
 * and the parent would lose the one they picked. Consumed once by CreateStoryPage on /create?fresh=1.
 */
const CREATE_DRAFT_KEY = 'snugtale_create_draft';
const CREATE_DRAFT_TTL_MS = 30 * 60 * 1000;
/** The trial's theme cap (backend AnonStoryCreate.theme ≤ 500) — the first run's idea came from that composer. */
const CREATE_DRAFT_MAX = 500;

export function stashCreateDraft(text: string | null): void {
  try {
    const v = (text ?? '').trim();
    if (!v) { sessionStorage.removeItem(CREATE_DRAFT_KEY); return; }
    sessionStorage.setItem(CREATE_DRAFT_KEY, JSON.stringify({ text: v.slice(0, CREATE_DRAFT_MAX), ts: Date.now() }));
  } catch { /* ignore */ }
}

/** Returns the stashed idea text once, or null when absent, malformed or stale. */
export function consumeCreateDraft(): string | null {
  try {
    const raw = sessionStorage.getItem(CREATE_DRAFT_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(CREATE_DRAFT_KEY);
    const p = JSON.parse(raw) as { text?: unknown; ts?: unknown };
    if (typeof p.ts !== 'number' || Date.now() - p.ts > CREATE_DRAFT_TTL_MS) return null;
    return typeof p.text === 'string' && p.text.trim() ? p.text.trim() : null;
  } catch {
    return null;
  }
}

/** Returns true (once) if an account was created within the last few minutes. */
export function consumeFreshAccount(): boolean {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return false;
    sessionStorage.removeItem(KEY);
    const ts = parseInt(raw, 10);
    return Number.isFinite(ts) && Date.now() - ts < TTL_MS;
  } catch {
    return false;
  }
}
